import React, { useEffect, useCallback, useState } from 'react'
import { Form, Spinner } from 'react-bootstrap'
import styled from 'styled-components'
import { DeleteIcon } from '../../Icons/DeleteIcon'
import useStores from '../../../stores/useStore'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react'
import { emptyGuid } from '../../../utils/emptyGuid'
import { isNumeric, newGuid } from '../../../utils/objectutils'
import { ButtonWithLoadingState } from '../../Buttons'
import useMountEffect from '../../../utils/hooks/useMountEffect'
import { cloneDeep } from 'lodash'
import { APIEndpoints } from '../../../utils/api/apiConfig'
import apiClient from '../../../utils/api/apiClient'

const StyledTr = styled.tr`
  &:hover {
    background-color: rgba(0, 0, 0, 0.075);
  }
  border-top: ${(props) => (props.borderTop ? '1px solid #e0e0e0' : '')};
`
const StyledExpandedRow = styled.div`
  display: flex;
  margin-left: 2rem;
`

const StyledExpandedRowInner = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  flex-grow: 1;
  .part1 {
    display: flex;
    flex-basis: 50%;
    padding: 0.5rem;
    align-items: flex-start;
    @media screen and (max-width: 992px) {
      flex-basis: 100%;
    }
  }
  .part2 {
    display: flex;
    flex-basis: 30%;
    padding: 0.5rem;
    @media screen and (max-width: 992px) {
      flex-basis: 100%;
    }
  }
`
const instanceTemplate = {
    id: emptyGuid,
    serialKey: '',
    batchKey: '',
    rfid: '',
    quantity: 1,
    new: true,
}
function newInstanceObj(quantity = 1) {
    const clonedTemplate = cloneDeep(instanceTemplate)
    clonedTemplate.id = newGuid()
    clonedTemplate.quantity = quantity
    return clonedTemplate
}

const ExpandedPosition = observer(({ position, article }) => {
    const { t } = useTranslation()
    const { productsStore } = useStores()
    const [instancesAreLoading, setInstancesAreLoading] = useState(false)
    const [posInstances, setPosInstances] = useState([])

    const loadInstances = async () => {
        setInstancesAreLoading(true)
        let localInstances = await productsStore.loadShoppingBasketArticlePositionInstances(
            article.id,
            position.id
        )
        setPosInstances(localInstances.instances)
        if (localInstances.quantity > (localInstances.instances?.length ?? 0)) {
            let posInstances = localInstances.instances ?? []
            let newInstances = [...posInstances]
            let addIQ = localInstances.quantity - newInstances.length
            if (
                article.article.useSerialKeys ||
                article.article.useRFIDs
            ) {
                for (let i = 0; i < addIQ; i++) {
                    newInstances.push(newInstanceObj())
                }
            } else if (!(localInstances.instances?.length ?? 0)) {
                //batchKey
                newInstances.push(newInstanceObj())
            }
            setPosInstances(newInstances)
        }
        setInstancesAreLoading(false)
    }

    useMountEffect(() => {
        loadInstances()
    })
    useEffect(() => {
        productsStore.updateLocalPositionInstances(article.id, position.id, posInstances)
      }, [posInstances, article.id, position.id, productsStore])

    const handleOnChange = (e, row, index) => {
        const { name, value } = e.target
        let newValue = value
        let newInstances = [...posInstances]
        if (name === 'quantity') {
            let oldValue = row.quantity
            let counted = parseInt(!isNumeric(newValue) ? oldValue : newValue)
            if (counted < 0) {
                counted = oldValue
            }
            if ((row.serialkey || row.rfid) && counted > 1) {
                counted = 1
            }
            let foundIndex = newInstances.findIndex((item) => item.id === row.id)
            if (foundIndex >= 0) {
                newInstances[foundIndex].quantity = counted
                setPosInstances(newInstances)
            }
        }
        else {
            let foundIndex = newInstances.findIndex((item) => item.id === row.id)
            if (foundIndex >= 0) {
                newInstances[foundIndex][name] = newValue
                setPosInstances(newInstances)
            }
        }
    };
    const handleDelete = useCallback(
        async (row, index) => {
            if (row.new) {
                let foundIndex = posInstances.findIndex((item) => item.id === row.id)
                if (foundIndex >= 0) {
                    const newInstances = [...posInstances]
                    newInstances.splice(foundIndex, 1)
                    setPosInstances(newInstances)
                }
            } else {
                const { basket } = productsStore
                let shoppingBasketArticleId = article.id
                let positionId = position.id
                let params = {
                    deletedInstances: [row.id],
                }
                try {
                    let result = await apiClient.patchJson(
                        APIEndpoints.shoppingBasketArticlePositionInstancesForPosition(
                            basket.id,
                            shoppingBasketArticleId,
                            positionId
                        ),
                        params
                    )
                    productsStore.updatePositionInstances(shoppingBasketArticleId, positionId, result.instances)
                    let foundIndex = posInstances.findIndex((item) => item.id === row.id)
                    if (foundIndex >= 0) {
                        const newInstances = [...posInstances]
                        newInstances.splice(foundIndex, 1)
                        setPosInstances(newInstances)
                    }
                } catch (error) {
                    console.log('deletedInstances>error>', error)
                }
            }
        },
        [posInstances, article.id, position.id, productsStore]
    )
    const handleAddNewInstance = () => {
        let newInstances = [...posInstances]
        newInstances.push(newInstanceObj())
        setPosInstances(newInstances)
    }
    const handleOnKeyDown = async (e) => {
        if (e.key === 'Enter') {
            e.preventDefault() //ENTER key on keyboard and page refreshes
            const { id, name } = e.target;
            const allTagInputs = document.getElementsByTagName("input");//=>allInputs is a HTMLCollection not an Array!!!
            let allNameInputs = [];
            for (let i = 0; i < allTagInputs.length; i++) {
                if (allTagInputs[i].id.includes(`${name}_`)) {
                    allNameInputs.push(allTagInputs[i]);
                }
            }
            let foundIndex = allNameInputs.findIndex(item => item.id === id)
            let nextfield = (foundIndex >= 0 && allNameInputs[foundIndex + 1]) ? allNameInputs[foundIndex + 1] : allNameInputs[0]
            // If found, focus the next field
            if (nextfield !== null) {
                nextfield.focus();
            }
        }
    }
    const renderPart1 = (row, index) => {
        let rowId = `${article.id}-${position.articleVariant ? position.articleVariant.id : emptyGuid}`
        let showSerialKeys = article.article.useSerialKeys;
        let showRFIDs = article.article.useRFIDs;
        return (showSerialKeys ?
            <>
                <Form.Control id={`${rowId}-sn_${index}`} name="sn" placeholder={t("localInventoriesTable:serialkey")} onChange={(e) => handleOnChange(e, row, index)} onKeyDown={handleOnKeyDown} value={row.serialKey ?? ""} />
            </>
            : showRFIDs ?
                <>
                    <Form.Control id={`${rowId}-rfid_${index}`} name="rfid" placeholder={t("inventory:scanCode")} onChange={(e) => handleOnChange(e, row, index)} onKeyDown={handleOnKeyDown} value={row.rfid ?? ""} />
                </>
                : <div></div>
        )
    }

    const renderPart2 = (row, index) => {
        let rowId = `${article.id}-${position.articleVariant ? position.articleVariant.id : emptyGuid}`
        let showBatchKeys = !article.article.useSerialKeys && article.article.useBatchKeys;
        return (
            <>
                {showBatchKeys ? (
                    <>
                        <Form.Control id={`${rowId}-bn_${index}`} name="bn" placeholder={t("localInventoriesTable:batchKey")} onChange={(e) => handleOnChange(e, row, index)} onKeyDown={handleOnKeyDown} value={row.batchKey ?? ""} />
                        <Form.Control id={`${rowId}-quantity_${index}`} name="quantity" placeholder={t("VariantQuantityTable:amount")} onChange={(e) => handleOnChange(e, row, index)} onKeyDown={handleOnKeyDown} value={row.quantity ?? 1} />
                    </>
                ) : null}
                <div
                    className='m-2'
                    role='button'
                    onClick={() => handleDelete(row, index)}
                >
                    <DeleteIcon title={t('delete')} />
                </div>
            </>
        )
    }

    const renderRows = () => {
        return posInstances?.map((row, index) => (
            <StyledExpandedRow key={index}>
                {<div className='m-2'>{index + 1}.</div>}
                <StyledExpandedRowInner>
                    <div className='part1'>{renderPart1(row, index)}</div>
                    <div className='part2'>{renderPart2(row, index)}</div>
                </StyledExpandedRowInner>
            </StyledExpandedRow>
        ))
    }
    return (
        <StyledTr className='bg-light'>
            <td colSpan='4'>
                {instancesAreLoading ? (
                    <div className='d-flex align-items-center justify-content-center'>
                        <Spinner animation='border' variant='primary' />
                    </div>
                ) : (
                    <div>
                        {renderRows()}
                    </div>
                )}
                <div align="right">
                    <ButtonWithLoadingState
                        variant='primary'
                        buttonText={`${t('add')}`}
                        onClick={handleAddNewInstance}
                    />
                </div>
            </td>
        </StyledTr>
    )
})
export default ExpandedPosition
